import { useCallback, useState } from 'react';
import { debounce } from 'throttle-debounce';

import { useHttpApi } from './useHttpApi';
import { useFormTracking } from '../../../utils/useFormTracking';
import {
  EQueryParameters,
  fetchValidateAddressWeb,
  TValidateAddressWebResponse,
} from '../../../repositories/geo-suggest/v3/validate-address-web';
import { isNewValidateRulesResponse } from './guards';

export const VALIDATION_ERROR = 'Укажите дом';
export const SERVER_ERROR = 'Не удалось получить данные о доме, попробуй ввести другой адрес';

export interface IUseValidateAddress {
  onHouseIdChange(value: number | null): void;
  onLocationIdChange(value: number | null): void;
  onStreetChange(value: number | null): void;
  objectType: EQueryParameters;
}
export const useValidateAddress = ({
  onHouseIdChange,
  onLocationIdChange,
  onStreetChange,
  objectType,
}: IUseValidateAddress) => {
  const httpApi = useHttpApi();
  const [isFetching, setIsFetching] = useState(false);
  const [validateError, setValidateError] = useState('');
  const { trackAddressFail } = useFormTracking();

  const resetValidation = useCallback(() => {
    onHouseIdChange(null);
    onLocationIdChange(null);
    onStreetChange(null);
    setValidateError('');
  }, [onHouseIdChange, onLocationIdChange]);

  const validateAddress = useCallback(
    async (address: string) => {
      if (!httpApi) {
        return;
      }

      resetValidation();

      setIsFetching(true);

      try {
        const validateResponse: TValidateAddressWebResponse = await fetchValidateAddressWeb({
          httpApi,
          parameters: { address, objectType },
        });

        if (validateResponse.statusCode !== 200) {
          trackAddressFail();
          setValidateError(/* istanbul ignore next */ validateResponse.response.message || VALIDATION_ERROR);

          return;
        }

        const { houseId = null, locationId = null } = validateResponse.response;
        onHouseIdChange(houseId);
        onLocationIdChange(locationId);

        if (isNewValidateRulesResponse(validateResponse)) {
          onStreetChange(validateResponse.response.streetId || null);
        }
      } catch (e) {
        setValidateError(SERVER_ERROR);
      } finally {
        setIsFetching(false);
      }
    },
    [httpApi, resetValidation, onHouseIdChange, onLocationIdChange, onStreetChange, trackAddressFail],
  );

  // oxlint-disable-next-line react/exhaustive-deps
  const validateAddressDebounced = useCallback(debounce(100, validateAddress), [validateAddress]);

  return {
    validateAddress: validateAddressDebounced,
    resetValidation,
    isFetching,
    validateError,
  };
};
